/**
 * feasibilityStandfirst — derive the editorial STANDFIRST (lede) for a
 * feasibility section from REAL report fields (N1, Editorial Report Redesign
 * v2.0).
 *
 * The editorial monograph opens each feature with an italic serif standfirst
 * that states the section's conclusion up front. To honour the "zero
 * unverified" North Star, the standfirst is NEVER fabricated — it is the
 * section's own already-generated "overview" / lead narrative, lifted verbatim
 * (citation tokens stripped, clamped to the opening sentence so it reads as a
 * lede, not a paragraph). When a section has no distinct overview field — or
 * the overview is the SAME text already rendered as the section's first body
 * block (e.g. gridStudy's `interconnectionAssessment`) — this returns
 * `undefined` so the shell renders NO standfirst rather than duplicating prose
 * or inventing one.
 *
 * Pure / deterministic / side-effect-free; safe in render.
 */
import type { SiteRagReport } from '../types/siteRag';

/** Strip inline `[Ref N]` / `[unverified …]` citation tokens for a clean lede. */
function stripCitations(text: string): string {
  return text
    .replace(/\[(?:Ref|ref)\s*\d+(?:\s*,\s*\d+)*\]/g, '')
    .replace(/\[unverified[^\]]*\]/gi, '')
    // Collapse the whitespace a removed token left BEFORE punctuation (e.g.
    // "excellent [Ref 3]." → "excellent ." → "excellent.").
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Clamp to the opening sentence (or ~220 chars) so the standfirst reads as a
 * one-line lede above the body, not a duplicated paragraph. Keeps the trailing
 * period; never cuts mid-word.
 */
function leadSentence(text: string): string {
  const clean = stripCitations(text);
  if (clean.length === 0) return '';
  // First sentence boundary (., !, ?) followed by whitespace + capital/space.
  const m = clean.match(/^[\s\S]*?[.!?](?=\s|$)/);
  let lede = m ? m[0].trim() : clean;
  if (lede.length > 220) {
    const cut = lede.slice(0, 220);
    const lastSpace = cut.lastIndexOf(' ');
    lede = `${cut.slice(0, lastSpace > 120 ? lastSpace : 220).trim()}…`;
  }
  return lede;
}

/**
 * Resolve the standfirst text for a feasibility section id, or `undefined` when
 * no REAL, non-duplicating overview exists.
 *
 * - transportStudy → `report.transportStudy.overview` (the combined Logistics &
 *   Connectivity section; the overview is a true summary, distinct from the
 *   road/rail/port body blocks).
 * - connectivityStudy → `report.connectivityStudy.overview` (archived standalone).
 *
 * gridStudy / riskAssessment / actionPlan intentionally have NO standfirst: their
 * lead narrative is the first body block (duplication risk) or they have no
 * summary field. Adding a fabricated lede there would violate the North Star.
 */
export function resolveFeasibilityStandfirst(
  report: Pick<SiteRagReport, 'transportStudy' | 'connectivityStudy'>,
  sectionId: string,
): string | undefined {
  let source: string | undefined;
  switch (sectionId) {
    case 'transportStudy':
      source = report.transportStudy?.overview;
      break;
    case 'connectivityStudy':
      source = report.connectivityStudy?.overview;
      break;
    default:
      return undefined;
  }
  if (typeof source !== 'string') return undefined;
  const lede = leadSentence(source);
  return lede.length > 0 ? lede : undefined;
}
